/**
 * useRealtimeInvalidation — connects to the backend WebSocket and
 * automatically invalidates TanStack Query cache when other users
 * modify data in the same tenant.
 *
 * Usage: call once in a top-level layout component (e.g. the
 * authenticated dashboard layout). The hook manages the WS
 * connection lifecycle — connects on mount, reconnects on drop,
 * disconnects on unmount.
 *
 * When a message arrives (e.g. `{ entity: "hr.getDepartments" }`),
 * the hook invalidates all queries whose key starts with the
 * entity prefix. TanStack Query handles the refetch automatically
 * — active queries refetch immediately, inactive ones are marked
 * stale for the next time they're used.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { BACKEND_URL } from '@/lib/orpcClient'

interface InvalidationMessage {
  entity: string
  id?: string | number
  ts: number
}

const RECONNECT_DELAY = 3000
const MAX_RECONNECT_DELAY = 30000

export function useRealtimeInvalidation() {
  const queryClient = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectDelayRef = useRef(RECONNECT_DELAY)
  const isUnmountedRef = useRef(false)

  useEffect(() => {
    isUnmountedRef.current = false

    function connect() {
      if (isUnmountedRef.current) return

      // Build the WS URL from the backend URL
      const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + '/ws'

      try {
        const ws = new WebSocket(wsUrl)
        wsRef.current = ws

        ws.onopen = () => {
          // Reset reconnect delay on successful connection
          reconnectDelayRef.current = RECONNECT_DELAY
        }

        ws.onmessage = (event) => {
          if (isUnmountedRef.current) return
          try {
            const msg: InvalidationMessage = JSON.parse(event.data)
            if (!msg.entity) return

            // Invalidate all queries that match the entity prefix.
            // oRPC query keys have the structure:
            //   [['hr', 'getDepartments'], { type: 'query', input: ... }]
            //
            // We split the entity string on '.' to get the path array,
            // then wrap it in another array so it prefix-matches the
            // oRPC key format.
            const keyParts = msg.entity.split('.')
            void queryClient.invalidateQueries({
              queryKey: [keyParts],
            })
          } catch {
            // Malformed message — ignore
          }
        }

        ws.onclose = () => {
          wsRef.current = null
          if (isUnmountedRef.current) return
          // Reconnect with exponential backoff
          reconnectTimerRef.current = setTimeout(() => {
            reconnectDelayRef.current = Math.min(
              reconnectDelayRef.current * 1.5,
              MAX_RECONNECT_DELAY,
            )
            connect()
          }, reconnectDelayRef.current)
        }

        ws.onerror = () => {
          // onclose will fire after onerror — reconnect happens there
        }
      } catch {
        // WebSocket constructor can throw if URL is invalid
        if (!isUnmountedRef.current) {
          reconnectTimerRef.current = setTimeout(connect, reconnectDelayRef.current)
        }
      }
    }

    connect()

    return () => {
      isUnmountedRef.current = true

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }

      const ws = wsRef.current
      wsRef.current = null

      if (!ws) return

      // Detach listeners so onclose doesn't re-trigger a reconnect
      // after cleanup, and onmessage doesn't touch the query cache
      // on a torn-down provider tree.
      ws.onopen = null
      ws.onmessage = null
      ws.onerror = null
      ws.onclose = null

      if (ws.readyState === WebSocket.OPEN) {
        ws.close()
      } else if (ws.readyState === WebSocket.CONNECTING) {
        // Closing a CONNECTING socket directly emits a browser warning
        // ("closed before established"). Instead, wait until the socket
        // opens, then close it cleanly. Common in React strict mode's
        // double-mount: first mount creates the socket, cleanup fires
        // before it connects.
        ws.onopen = () => ws.close()
      }
      // readyState CLOSING (2) or CLOSED (3): nothing to do
    }
  }, [queryClient])
}
